import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ClimbStandingIcon } from '../climbs/ClimbStandingIcon';
import { isDrawableModelLine } from '../crag/useModelLine';
import { cn } from '../../lib/utils';
import type { Climb } from '../../types/climb';

const TICK_WIDTH = 24;
const lineVariants = {
  normal: { width: TICK_WIDTH },
  active: { width: 40 },
  hover: { width: 40 },
};

interface CragRouteLineNavProps {
  climbs: Climb[];
  activeClimbId: string | null;
  /** The entry that is already active asks for this too: that is how one deselects. */
  onToggle: (climbId: string) => void;
  /** Whether each entry carries the reader's standing: signed in, so it means something. */
  showStanding: boolean;
  /**
   * Ticks and the active label only, leaving the model in view. A press anywhere on
   * it then opens the labels rather than selecting a route nobody could read.
   */
  folded: boolean;
  onUnfold: () => void;
  className?: string;
}

/**
 * The sector's routes down the left of its 3D model: a tick per route, the active
 * one longer. Every route is here, drawn on the model or not, because the floating
 * button acts on whichever is selected: a route with no line has a dashed tick.
 *
 * Adapted from Line Nav by ncdai (https://chanhdai.com/components/line-nav, MIT):
 * buttons rather than anchors, white over the rock whatever the theme, and folding.
 */
export function CragRouteLineNav({
  climbs,
  activeClimbId,
  onToggle,
  showStanding,
  folded,
  onUnfold,
  className,
}: CragRouteLineNavProps) {
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Scrolled by hand: scrollIntoView would move the page as well if it has anywhere to go.
  useEffect(() => {
    const nav = navRef.current;
    const active = activeRef.current;
    if (!nav || !active) return;
    const box = nav.getBoundingClientRect();
    const row = active.getBoundingClientRect();
    const middle = row.top + row.height / 2 - box.top;
    if (middle < 24 || middle > box.height - 24) nav.scrollTop += middle - box.height / 2;
  }, [activeClimbId]);

  return (
    <nav
      ref={navRef}
      aria-label={t('cragProfile.model3d.routesNav')}
      data-testid="crag-route-line-nav"
      data-folded={folded}
      className={cn(
        'touch-pan-y overflow-y-auto overscroll-contain rounded-br-2xl py-5 pl-3 pr-8 [scrollbar-width:none]',
        // Folded, whatever is not a tick lets the orbit through.
        folded
          ? 'pointer-events-none'
          : 'pointer-events-auto bg-gradient-to-r from-black/50 via-black/30 to-transparent',
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        {climbs.map((climb, index) => {
          const active = climb.id === activeClimbId;
          const drawn = isDrawableModelLine(climb.model_line);
          return (
            <div key={climb.id} className="contents">
              <motion.button
                ref={active ? activeRef : undefined}
                type="button"
                aria-current={active ? 'true' : undefined}
                data-testid="crag-route-line-nav-item"
                data-drawn={drawn}
                onClick={() => (folded ? onUnfold() : onToggle(climb.id))}
                initial={false}
                animate={active ? 'active' : 'normal'}
                whileHover="hover"
                className="group pointer-events-auto relative flex h-0.5 w-fit items-center gap-2 text-left after:absolute after:left-0 after:top-1/2 after:size-full after:-translate-y-1/2 after:p-3.5"
              >
                {showStanding && <ClimbStandingIcon climb={climb} className="h-3.5 w-3.5 shrink-0 drop-shadow" />}
                <motion.span
                  className={cn(
                    'block shrink-0 shadow-[0_0_2px_rgba(0,0,0,0.8)] transition-colors group-hover:bg-white group-aria-[current=true]:bg-white',
                    drawn ? 'h-0.5 bg-white/60' : 'h-0 border-t-2 border-dashed border-white/70 shadow-none',
                  )}
                  variants={lineVariants}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                />
                <span
                  className={cn(
                    'flex max-w-[40vw] items-baseline gap-1.5 whitespace-nowrap text-sm text-white/85 transition-colors [text-shadow:0_1px_2px_rgba(0,0,0,0.9)] group-hover:text-white group-aria-[current=true]:font-medium group-aria-[current=true]:text-white md:max-w-xs',
                    // Not display:none: a screen reader still needs every name.
                    folded && !active && 'sr-only',
                  )}
                >
                  <span className="truncate">{climb.name}</span>
                  {climb.difficulty && <span className="shrink-0 text-xs text-white/70">{climb.difficulty}</span>}
                </span>
              </motion.button>

              {index < climbs.length - 1 && (
                <>
                  <Spacer showStanding={showStanding} />
                  <Spacer showStanding={showStanding} />
                </>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

/** The shorter lines between two routes, under the ticks rather than under the icons. */
function Spacer({ showStanding }: { showStanding: boolean }) {
  return (
    <span
      className={cn('block h-px bg-white/30 shadow-[0_0_1px_rgba(0,0,0,0.6)]', showStanding && 'ml-[1.375rem]')}
      style={{ width: TICK_WIDTH }}
    />
  );
}
